import { useSyncExternalStore } from 'react';
import { VoiceSfuSession, type SfuHost } from './voiceSfu';
import { VoiceScreenShareSession, type ScreenShareHost } from './voiceScreenShare';
import { VoiceCameraSession, type CameraHost } from './voiceCamera';
import {
  getVoicePreferences,
  subscribeVoicePreferences,
  type ScreenFps,
  type ScreenResolution,
} from '@/lib/voicePreferences';
import {
  anyStageEnabled,
  buildProcessedAudio,
  getSharedAudioContextState,
  onAudioContextRunning,
  type ProcessedAudio,
} from '@/lib/voice/audioGraph';
import {
  buildAudioConstraints,
  isOverconstrainedDeviceError,
  normalizeMediaError,
  resolveAudioProcessingMode,
  stripDeviceId,
  type AudioProcessingMode,
} from '@/lib/voice/voiceMediaHelpers';
import { DeviceChangeWatch, micSwapReason } from '@/lib/voice/micDevices';
import type { VoiceUserState } from '@/types/api';
import type { VoiceDiagnostics } from './voiceDiagnostics';

/** Voice-media error: /api/rtc/config never answered (see markRtcUnavailable). */
export const RTC_CONFIG_UNAVAILABLE = 'rtc_config_unavailable';

export interface RemoteVoiceStream {
  userId: string;
  stream: MediaStream;
  mediaTag: SfuMediaTag;
}

export type SfuMediaTag = 'microphone' | 'camera' | 'screen' | 'screen-audio';

interface VoiceMediaSnapshot {
  activeChannelId: string | null;
  localStream: MediaStream | null;
  localCameraStream: MediaStream | null;
  localScreenStream: MediaStream | null;
  remoteStreams: RemoteVoiceStream[];
  muted: boolean;
  deafened: boolean;
  error: string | null;
  // True when getUserMedia() returned NotAllowedError — user is in the
  // room as a listener (can hear others + share screen) but can't
  // produce audio. UI surfaces this as a permission banner; the rest of
  // the voice pipeline treats audioTrack=null as "skip producing".
  micDenied: boolean;
  // The call is getting its connection back (network change, socket drop).
  // Short blips heal before this turns on; the UI may say «Восстанавливаем связь…».
  reconnecting: boolean;
}

const DEFAULT_SNAPSHOT: VoiceMediaSnapshot = {
  activeChannelId: null,
  localStream: null,
  localCameraStream: null,
  localScreenStream: null,
  remoteStreams: [],
  muted: false,
  deafened: false,
  error: null,
  micDenied: false,
  reconnecting: false,
};

class VoiceMediaController {
  private snapshot: VoiceMediaSnapshot = DEFAULT_SNAPSHOT;
  private listeners = new Set<() => void>();

  constructor() {
    // Reapply mic state whenever the user toggles a filter or changes the
    // microphone in settings. A device change needs a full re-acquire
    // (applyConstraints can't switch the physical device); everything else is
    // a cheap constraint/graph update. Singleton — no need to track unsubscribe.
    subscribeVoicePreferences(() => {
      if (getVoicePreferences().inputDeviceId !== this.appliedInputDeviceId) {
        void this.switchInputDevice();
        return;
      }
      void this.applyAudioConstraints();
      void this.onVoicePrefsChanged();
    });
  }
  private myUserId: string | null = null;
  private debugEvents: string[] = [];
  private localStreamPromise: Promise<MediaStream> | null = null;
  // Bumped by leave(). A getUserMedia that resolves after the call it was
  // opened for has ended must not become the local stream or start the SFU.
  private callEpoch = 0;
  private audioBitrateKbps = 96;
  private audioProcessingMode: AudioProcessingMode = 'voice';
  // Local mic processing graph («Как в бункере» / нейрошумодав). When no
  // stage is enabled, `productionTrack` is the raw mic track and `audioGraph`
  // is null — the zero-filter path is unchanged.
  private audioGraph: ProcessedAudio | null = null;
  private productionTrack: MediaStreamTrack | null = null;
  // Signature of the filter stages the current graph was built for. Lets
  // onVoicePrefsChanged skip a costly graph rebuild + replaceTrack when an
  // unrelated voice pref changes (output gain slider, browser flags, …).
  private appliedStageKey: string | null = null;
  // DeepFilterNet intensity the live graph was built/tuned with. Changing
  // only this is hot-applied via the worklet port — no rebuild needed.
  private appliedDfnetAtten: number | null = null;
  // Input gain is live-tunable on the graph's GainNode. It is kept separately
  // from the stage key so moving the slider does not recreate an SFU track.
  private appliedInputGain: number | null = null;
  // The inputDeviceId the current localStream was captured with. Lets the
  // prefs subscription tell a device switch (needs re-acquire) from an
  // unrelated tweak. Seeded each time a fresh mic stream is acquired.
  private appliedInputDeviceId: string | null = null;
  // Voice-activation gate: true = currently allowed to transmit. In open-mic
  // mode it stays true; in voice-activation mode VoiceAudioLayer toggles it
  // from the local speaking detector. Combined with `muted` in applyTransmit.
  private micGateOpen = true;
  // Voice media runs only through the SFU (mediasoup). Nothing connects until
  // /api/rtc/config has answered (VoiceAudioLayer retries it): before that the
  // channel is only remembered, and if the config never comes the call shows
  // RTC_CONFIG_UNAVAILABLE instead of falling back to anything else.
  private rtcReady = false;
  private rtcUnavailable = false;
  // The SFU lifecycle lives in its own module; this controller delegates to
  // the session, which reaches back only through the narrow SfuHost below.
  private sfu = new VoiceSfuSession({
    ensureLocalStream: () => this.ensureLocalStream(),
    getProductionTrack: () => this.getProductionTrack(),
    getActiveChannelId: () => this.snapshot.activeChannelId,
    getMyUserId: () => this.myUserId,
    isMediaReady: () => this.rtcReady,
    getAudioProcessingMode: () => this.audioProcessingMode,
    setRemoteStreams: (remoteStreams) => this.patch({ remoteStreams }),
    setError: (error) => this.setError(error),
    setReconnecting: (reconnecting) => {
      if (this.snapshot.reconnecting !== reconnecting) this.patch({ reconnecting });
    },
    recordDebug: (event) => this.recordDebug(event),
    stopLocalScreenStream: () => this.screenShare.stopLocalStream(),
  } satisfies SfuHost);

  // Screen-share capture (SFU-only) lives in its own module; this controller
  // delegates capture + producer lifecycle to it through the ScreenShareHost
  // interface below. The owned `screenAudioStop` state moved in with it.
  private screenShare = new VoiceScreenShareSession({
    getActiveChannelId: () => this.snapshot.activeChannelId,
    isMediaReady: () => this.rtcReady,
    getLocalScreenStream: () => this.snapshot.localScreenStream,
    onScreenStreamStarted: (stream) => this.patch({ localScreenStream: stream, error: null }),
    onScreenStreamStopped: () => {
      if (this.snapshot.localScreenStream) this.patch({ localScreenStream: null });
    },
    recordDebug: (event) => this.recordDebug(event),
    produceScreen: (channelId, track, maxBitrate) =>
      this.sfu.produceSfuScreen(channelId, track, maxBitrate),
    produceScreenAudio: (channelId, track) => this.sfu.produceSfuScreenAudio(channelId, track),
    closeScreenProducer: (channelId) => this.sfu.closeSfuScreenProducer(channelId),
    closeScreenAudioProducer: (channelId) => this.sfu.closeSfuScreenAudioProducer(channelId),
    replaceScreenAudioTrack: (track) => this.sfu.replaceSfuScreenAudioTrack(track),
    updateScreenMaxBitrate: (maxBitrate) => this.sfu.updateSfuScreenMaxBitrate(maxBitrate),
    getScreenSenderStats: () => this.sfu.getSfuScreenSenderStats(),
  } satisfies ScreenShareHost);

  // Camera (local video) capture lives in its own module; this controller
  // delegates the toggle lifecycle to it through the CameraHost interface.
  private camera = new VoiceCameraSession({
    isMediaReady: () => this.rtcReady,
    getActiveChannelId: () => this.snapshot.activeChannelId,
    getLocalCameraStream: () => this.snapshot.localCameraStream,
    onCameraStreamStarted: (stream) => this.patch({ localCameraStream: stream, error: null }),
    onCameraStreamStopped: () => {
      if (this.snapshot.localCameraStream) this.patch({ localCameraStream: null });
    },
    produceVideo: (channelId, track) => this.sfu.produceSfuVideo(channelId, track),
    closeVideoProducer: (channelId) => this.sfu.closeSfuVideoProducer(channelId),
  } satisfies CameraHost);

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    // Block body so the return resolves to `void` — Set.delete returns
    // a boolean which violates the `useSyncExternalStore` cleanup contract
    // (its identity check trips and StrictMode reinstalls subscriptions).
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = () => this.snapshot;

  getDiagnostics(): Promise<VoiceDiagnostics> {
    return this.sfu.diagnostics();
  }

  async activateChannel(channelId: string, myUserId: string, audioBitrateKbps = 96): Promise<void> {
    // Taken before the first await: leaving during any await below must not
    // take the microphone or start the SFU afterwards.
    const epoch = this.callEpoch;
    this.recordDebug(`activate:${channelId}:${myUserId}`);
    this.myUserId = myUserId;
    await this.setAudioBitrateKbps(audioBitrateKbps);
    if (this.callEpoch !== epoch) return;
    if (this.snapshot.activeChannelId && this.snapshot.activeChannelId !== channelId) {
      this.recordDebug(`activate:switch:${this.snapshot.activeChannelId}->${channelId}`);
      this.patch({ remoteStreams: [], error: null });
    }
    this.patch({ activeChannelId: channelId });
    // Without the RTC config nothing connects — not even the microphone.
    // markRtcReady() picks the channel up when the config arrives.
    if (!this.rtcReady) {
      if (this.rtcUnavailable) this.setError(RTC_CONFIG_UNAVAILABLE);
      return;
    }
    await this.ensureLocalStream();
    if (!this.stillActive(epoch, channelId)) return;
    await this.sfu.startSfu(channelId);
  }

  /** Still in the same call on the same channel as when an await began. */
  private stillActive(epoch: number, channelId: string): boolean {
    return this.callEpoch === epoch && this.snapshot.activeChannelId === channelId;
  }

  async syncParticipants(
    channelId: string,
    myUserId: string,
    participants: VoiceUserState[],
  ): Promise<void> {
    if (this.snapshot.activeChannelId !== channelId) return;
    this.recordDebug(`sync:${channelId}:participants=${participants.length}`);
    this.myUserId = myUserId;
    if (!this.rtcReady) return;
    await this.sfu.startSfu(channelId);
    await this.sfu.refreshSfuProducers(channelId);
  }

  /** The socket is back and the voice rejoin is about to go out. Returns
   *  whether to ask the server to keep our media session (see voiceResume). */
  beginVoiceResume(): boolean {
    return this.rtcReady && this.sfu.beginResume();
  }

  /** The rejoin answered: `resumed` — the server kept the media session. */
  finishVoiceResume(resumed: boolean): void {
    this.sfu.finishResume(resumed);
  }

  async ensureLocalStream(): Promise<MediaStream> {
    if (this.snapshot.localStream) return this.snapshot.localStream;
    // Listener mode (mic was previously denied) — hand back an empty
    // MediaStream. Downstream code treats audioTracks=[] as "nothing to
    // produce" and proceeds with consume-only + screen-share.
    if (this.snapshot.micDenied) {
      const empty = new MediaStream();
      this.patch({ localStream: empty });
      return empty;
    }
    if (!this.localStreamPromise) {
      const epoch = this.callEpoch;
      const pending: Promise<MediaStream> = this.acquireMicStream()
        .then((stream) => {
          // Left the call while the permission prompt was open: release the
          // mic right away instead of keeping it captured for nobody.
          if (this.callEpoch !== epoch) {
            for (const track of stream.getTracks()) track.stop();
            this.recordDebug('mic:late-after-leave:dropped');
            return new MediaStream();
          }
          for (const track of stream.getAudioTracks()) {
            track.enabled = !this.snapshot.muted;
          }
          // Fresh raw stream — drop any graph built on the previous one so
          // the next getProductionTrack() rebuilds against this stream.
          this.disposeAudioGraph();
          this.productionTrack = null;
          this.appliedStageKey = null;
          this.appliedInputDeviceId = getVoicePreferences().inputDeviceId;
          this.patch({ localStream: stream, error: null, micDenied: false });
          this.watchMicTrack(stream);
          return stream;
        })
        .catch((err) => {
          const normalized = normalizeMediaError(err, 'microphone');
          if (this.callEpoch !== epoch) return new MediaStream();
          // Permission denied is not fatal — flip to listener mode and
          // return an empty stream so the activation can proceed.
          if (normalized.message === 'microphone_permission_denied') {
            const empty = new MediaStream();
            this.patch({
              localStream: empty,
              micDenied: true,
              error: normalized.message,
            });
            return empty;
          }
          throw normalized;
        })
        .finally(() => {
          if (this.localStreamPromise === pending) this.localStreamPromise = null;
        });
      this.localStreamPromise = pending;
    }
    return this.localStreamPromise;
  }

  /** getUserMedia for the mic with the user's constraints, retrying once
   *  without the deviceId pin when the saved device is overconstrained
   *  (unplugged / id rotated / permission reset) so the user still gets the
   *  default mic instead of a hard failure. */
  private async acquireMicStream(): Promise<MediaStream> {
    const constraints = buildAudioConstraints(this.audioProcessingMode);
    try {
      return await navigator.mediaDevices.getUserMedia({ audio: constraints, video: false });
    } catch (err) {
      if (isOverconstrainedDeviceError(err) && 'deviceId' in constraints) {
        this.recordDebug('mic:overconstrained:fallback-default');
        return navigator.mediaDevices.getUserMedia({
          audio: stripDeviceId(constraints),
          video: false,
        });
      }
      throw err;
    }
  }

  /** Switch the live microphone when the user picks a different input device
   *  mid-call. applyConstraints can't change the physical device, so we
   *  re-acquire and hot-swap the track on the SFU producer
   *  (mirrors onVoicePrefsChanged's filter swap). No-op (just records the id)
   *  when not capturing — the next join's getUserMedia picks the choice up. */
  private switchInputDevice(): Promise<void> {
    const wantId = getVoicePreferences().inputDeviceId;
    if (!this.capturingMic()) {
      this.appliedInputDeviceId = wantId;
      return Promise.resolve();
    }
    return this.queueMicSwap(async () => {
      if (this.capturingMic()) await this.swapMicStream(`switch:${wantId}`);
    });
  }

  /** In a call with a real microphone (not listener mode). */
  private capturingMic(): boolean {
    const stream = this.snapshot.localStream;
    return !!stream && stream.getAudioTracks().length > 0 && !this.snapshot.micDenied;
  }

  // One mic swap at a time: unplugging a headset fires the track's `ended`
  // and `devicechange` together, and a settings change can land mid-swap.
  private micSwapChain: Promise<void> = Promise.resolve();

  private queueMicSwap(job: () => Promise<void>): Promise<void> {
    const next = this.micSwapChain.then(job, job);
    this.micSwapChain = next.catch(() => {});
    return next;
  }

  /** Re-acquire the microphone and hot-swap it on the SFU producer. On failure the current mic keeps running if it still can.
   *  Returns whether the swap happened. */
  private async swapMicStream(reason: string): Promise<boolean> {
    const wantId = getVoicePreferences().inputDeviceId;
    const oldStream = this.snapshot.localStream;
    let newStream: MediaStream;
    try {
      newStream = await this.acquireMicStream();
    } catch (err) {
      this.setError(normalizeMediaError(err, 'microphone').message);
      this.recordDebug(`mic:${reason}:failed`);
      return false;
    }
    // Left the call (or it restarted) while getUserMedia was pending.
    if (this.snapshot.localStream !== oldStream) {
      for (const track of newStream.getTracks()) track.stop();
      return false;
    }
    this.disposeAudioGraph();
    this.productionTrack = null;
    this.appliedStageKey = null;
    this.appliedInputDeviceId = wantId;
    this.patch({ localStream: newStream, error: null });
    this.watchMicTrack(newStream);
    for (const track of oldStream?.getTracks() ?? []) {
      track.stop();
    }
    this.applyTransmit();
    const track = await this.getProductionTrack();
    if (!track) return false;
    await this.sfu.replaceAudioTrack(track);
    this.recordDebug(`mic:${reason}`);
    return true;
  }

  // ── microphone loss and device changes (decisions in voice/micDevices) ────
  // The track's `ended` re-acquires right away (a saved device that's gone
  // falls back to the system mic); `devicechange` re-checks which mic to use.

  private deviceWatch = new DeviceChangeWatch(() => {
    void this.queueMicSwap(async () => {
      const track = this.snapshot.localStream?.getAudioTracks()[0];
      if (!track || this.snapshot.micDenied) return;
      const devices = await navigator.mediaDevices.enumerateDevices().catch(() => null);
      const reason = devices
        ? micSwapReason(track, devices, getVoicePreferences().inputDeviceId)
        : track.readyState === 'ended' ? 'recover' : null;
      if (reason) await this.swapMicStream(reason);
    });
  });

  private watchMicTrack(stream: MediaStream) {
    this.deviceWatch.bind();
    const track = stream.getAudioTracks()[0];
    if (!track) return;
    // `stop()` does not fire `ended`, so this is always the device going away.
    track.onended = () => {
      if (this.snapshot.localStream !== stream) return;
      this.recordDebug('mic:ended');
      void this.queueMicSwap(async () => {
        if (this.snapshot.localStream === stream) await this.swapMicStream('recover');
      });
    };
  }

  setMuted(muted: boolean) {
    this.patch({ muted });
    this.applyTransmit();
  }

  /** Voice-activation gate (called by VoiceAudioLayer from the local speaking
   *  detector). Open-mic mode keeps this true; manual mute always wins. */
  setMicGateOpen(open: boolean) {
    if (this.micGateOpen === open) return;
    this.micGateOpen = open;
    this.applyTransmit();
  }

  /**
   * Apply the effective transmit state. Two distinct mechanisms:
   *  - MUTE silences the source (raw track + graph) — fine, it's intentional.
   *  - The voice-activation GATE pauses/resumes the SFU producer WITHOUT
   *    disabling the track, so the local VAD analyser keeps reading the mic
   *    (disabling the track would deafen the detector → gate stuck closed).
   *    Voice activation therefore applies on the SFU backend.
   */
  private applyTransmit() {
    // Deafened never transmits (can't hear → don't talk), without flipping the
    // user's own mic choice — undeafening restores exactly what they had.
    const silent = this.snapshot.muted || this.snapshot.deafened;
    for (const track of this.snapshot.localStream?.getAudioTracks() ?? []) {
      track.enabled = !silent;
    }
    this.audioGraph?.setEnabled(!silent);

    const transmit = !silent && this.micGateOpen;
    this.sfu.setTransmitting(transmit);
  }

  /**
   * Retry the microphone permission flow after the user has granted
   * access at the OS/browser level. Called from the "Попробовать снова"
   * button in the listener-mode banner. Drops the empty placeholder
   * stream, clears the micDenied flag, and re-runs ensureLocalStream;
   * if it succeeds the SFU audio producer is set up on the existing
   * send transport so the user starts speaking without leaving/rejoining
   * the channel.
   */
  async retryMicrophone(): Promise<boolean> {
    if (!this.snapshot.micDenied) return true;
    // Discard the empty placeholder stream so ensureLocalStream falls
    // through to a fresh getUserMedia call instead of short-circuiting
    // on the cached stream.
    this.patch({ localStream: null, micDenied: false, error: null });
    const epoch = this.callEpoch;
    try {
      await this.ensureLocalStream();
      if (this.callEpoch !== epoch) return false;
      const audioTrack = await this.rebuildProductionTrack();
      if (!audioTrack) {
        // Still no track — getUserMedia succeeded but returned nothing.
        // Likely a hardware glitch; surface a generic error.
        this.patch({ error: 'microphone_access_failed' });
        return false;
      }
      // Hook the new track into existing SFU producer if we're already
      // in a channel. Without a producer, just attaching the track is
      // enough — connectSfu on next activate would notice it.
      if (this.rtcReady && this.snapshot.activeChannelId) {
        await this.sfu.retryAudioProduce(this.snapshot.activeChannelId, audioTrack);
      }
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'microphone_access_failed';
      this.patch({ error: msg });
      return false;
    }
  }

  setDeafened(deafened: boolean) {
    this.patch({ deafened });
    this.applyTransmit();
  }

  /** /api/rtc/config answered: voice may connect. Picks up a channel joined
   *  while the config was still on its way. */
  markRtcReady() {
    if (this.rtcReady) return;
    this.rtcReady = true;
    this.rtcUnavailable = false;
    this.recordDebug('rtc:ready');
    const channelId = this.snapshot.activeChannelId;
    if (!channelId || !this.myUserId) return;
    if (this.snapshot.error === RTC_CONFIG_UNAVAILABLE) this.setError(null);
    const epoch = this.callEpoch;
    void this.ensureLocalStream()
      .then(async () => {
        if (!this.stillActive(epoch, channelId)) return;
        await this.sfu.startSfu(channelId);
        await this.sfu.refreshSfuProducers(channelId);
      })
      .catch((err) => {
        this.setError(err instanceof Error ? err.message : 'sfu_start_failed');
      });
  }

  /** /api/rtc/config could not be fetched after every retry: voice stays
   *  disconnected and says so. A later markRtcReady() still connects. */
  markRtcUnavailable() {
    if (this.rtcReady) return;
    this.rtcUnavailable = true;
    this.recordDebug('rtc:unavailable');
    if (this.snapshot.activeChannelId) this.setError(RTC_CONFIG_UNAVAILABLE);
  }

  /** The config gave up (so a new join should ask for it again). */
  isRtcUnavailable(): boolean {
    return this.rtcUnavailable;
  }

  async setAudioBitrateKbps(kbps: number | null | undefined): Promise<void> {
    const nextKbps = Math.max(8, Math.min(384, Math.round(kbps ?? 96)));
    const nextMode = resolveAudioProcessingMode(nextKbps);
    if (this.audioBitrateKbps === nextKbps && this.audioProcessingMode === nextMode) return;
    this.audioBitrateKbps = nextKbps;
    this.audioProcessingMode = nextMode;
    await this.applyAudioConstraints();
  }

  setVideoEnabled(enabled: boolean): Promise<boolean> {
    return this.camera.setEnabled(enabled);
  }

  setScreenShareEnabled(enabled: boolean): Promise<boolean> {
    return this.screenShare.setEnabled(enabled);
  }

  /** Current screen-share quality state (chosen tier, auto flag, applied
   *  step). Valid UI data even when not sharing (falls back to prefs). */
  getScreenShareQuality() {
    return this.screenShare.getQualityState();
  }

  /** Change the screen-share tier mid-share (applyConstraints + bitrate
   *  retarget — no producer restart). Also persists as the preference. */
  setScreenShareQuality(step: { resolution: ScreenResolution; fps: ScreenFps }): Promise<void> {
    return this.screenShare.setQuality(step);
  }

  /** Toggle adaptive («Авто») screen-share quality mid-share. */
  setScreenShareAutoQuality(enabled: boolean): void {
    this.screenShare.setAutoQuality(enabled);
  }

  leave() {
    this.recordDebug('leave');
    this.callEpoch += 1;
    // A rejoin must not reuse a getUserMedia still pending for this call.
    this.localStreamPromise = null;
    this.deviceWatch.cancel();
    this.sfu.closeSfu();
    this.disposeAudioGraph();
    this.productionTrack = null;
    this.appliedStageKey = null;
    this.micGateOpen = true;
    for (const track of this.snapshot.localStream?.getTracks() ?? []) {
      track.stop();
    }
    this.camera.stopLocalStream();
    this.screenShare.stopLocalStream();
    this.snapshot = {
      ...DEFAULT_SNAPSHOT,
      muted: this.snapshot.muted,
      deafened: this.snapshot.deafened,
    };
    this.emit();
  }

  setError(error: string | null) {
    this.patch({ error });
  }

  private patch(partial: Partial<VoiceMediaSnapshot>) {
    this.snapshot = { ...this.snapshot, ...partial };
    this.emit();
  }

  private emit() {
    this.writeDebug();
    for (const listener of this.listeners) {
      listener();
    }
  }

  private async applyAudioConstraints() {
    const track = this.snapshot.localStream?.getAudioTracks()[0] ?? null;
    if (!track) return;
    await track.applyConstraints(buildAudioConstraints(this.audioProcessingMode)).catch((err) => {
      this.recordDebug(`audio:constraints:error:${err instanceof Error ? err.name : 'unknown'}`);
    });
    this.recordDebug(`audio:mode:${this.audioProcessingMode}`);
  }

  /**
   * The track we actually send: the raw mic track, or — when the user has
   * enabled a local processing stage («Как в бункере» / нейрошумодав) — the
   * output of the {@link buildProcessedAudio} graph. Cached; rebuilt on pref
   * change ({@link onVoicePrefsChanged}) or fresh mic stream.
   */
  private async getProductionTrack(): Promise<MediaStreamTrack | null> {
    if (this.productionTrack && this.productionTrack.readyState === 'live') {
      return this.productionTrack;
    }
    return this.rebuildProductionTrack();
  }

  private async rebuildProductionTrack(): Promise<MediaStreamTrack | null> {
    // Every rebuild re-evaluates whether recovery is needed; drop any armed
    // subscription so a stale one can't fire for a graph that's gone.
    this.disarmAudioContextRecovery();
    this.disposeAudioGraph();
    const stream = this.snapshot.localStream;
    const raw = stream?.getAudioTracks()[0] ?? null;
    if (!stream || !raw) {
      this.productionTrack = null;
      return null;
    }
    const prefs = getVoicePreferences();
    const stages = {
      bunker: prefs.bunkerFilter,
      rnnoise: prefs.neuralSuppression,
      dfnet: prefs.deepFilter,
      gain: prefs.inputGain !== 100,
    };
    this.appliedStageKey = `${stages.bunker}|${stages.rnnoise}|${stages.dfnet}|${stages.gain}`;
    this.appliedDfnetAtten = prefs.deepFilterAtten;
    this.appliedInputGain = prefs.inputGain;
    if (!anyStageEnabled(stages)) {
      this.productionTrack = raw;
      return raw;
    }
    try {
      this.audioGraph = await buildProcessedAudio(stream, stages, {
        dfnetAttenLim: prefs.deepFilterAtten,
        inputGain: prefs.inputGain,
      });
      // Keep the graph's cloned input in sync with the current transmit state.
      this.audioGraph?.setEnabled(!this.snapshot.muted && this.micGateOpen);
      this.productionTrack = this.audioGraph?.track ?? raw;
      this.recordDebug(`audio:graph:${this.audioGraph ? 'on' : 'raw'}`);
      // A graph built on a suspended AudioContext (first-join autoplay trap)
      // outputs a live-but-silent track. Arm a one-shot rebuild for the
      // moment the context actually starts running so listeners get audio
      // without the user having to rejoin.
      if (this.audioGraph && !this.audioGraph.contextRunning) {
        this.recordDebug('audio:graph:suspended-context');
        this.armAudioContextRecovery();
      }
    } catch (err) {
      // Never let a filter failure break voice — fall back to the raw track.
      // Surface it (prod too): a silent fallback once shipped "filters do
      // nothing" when a strict CSP blocked the worklet.
      this.recordDebug(`audio:graph:error:${err instanceof Error ? err.name : 'unknown'}`);
      console.warn('[bunker-audio] voice filter graph failed — sending raw mic:', err);
      this.audioGraph = null;
      this.productionTrack = raw;
    }
    return this.productionTrack;
  }

  private disposeAudioGraph() {
    this.audioGraph?.dispose();
    this.audioGraph = null;
  }

  // ── suspended-context recovery ─────────────────────────────────────────────
  // First-join autoplay trap: the shared AudioContext can still be suspended
  // when the mic graph is built (the join click's activation was consumed by
  // the socket round-trip + permission prompt), so the published track is
  // silent. primeAudioContext() in the join click prevents this for the
  // common case; this recovery handles anything that slips through — the
  // moment the context reaches `running` (any later user gesture resumes it),
  // rebuild the graph and hot-swap the live producers.

  private contextRecoveryUnsub: (() => void) | null = null;

  private disarmAudioContextRecovery() {
    this.contextRecoveryUnsub?.();
    this.contextRecoveryUnsub = null;
  }

  private armAudioContextRecovery() {
    if (this.contextRecoveryUnsub) return;
    if (getSharedAudioContextState() === 'running') {
      // Became running between the build and this call — recover right away.
      void this.recoverFromSuspendedContext();
      return;
    }
    const unsub = onAudioContextRunning(() => {
      unsub();
      this.contextRecoveryUnsub = null;
      void this.recoverFromSuspendedContext();
    });
    this.contextRecoveryUnsub = unsub;
  }

  private async recoverFromSuspendedContext() {
    // Only meaningful while a processed graph is live; a raw-track session or
    // a torn-down call has nothing to recover.
    if (!this.snapshot.localStream || !this.audioGraph) return;
    const track = await this.rebuildProductionTrack();
    if (!track) return;
    await this.sfu.replaceAudioTrack(track);
    this.recordDebug('audio:graph:recovered-from-suspended');
  }

  /** Rebuild the processing graph and hot-swap the live track on the SFU
   *  producer, so toggling a filter mid-call applies
   *  without rejoining. */
  private async onVoicePrefsChanged() {
    if (!this.snapshot.localStream) return;
    // Only rebuild when the filter stages actually changed — not on every
    // unrelated voice-pref tweak (output gain, browser flags, device id).
    const prefs = getVoicePreferences();
    const stageKey = `${prefs.bunkerFilter}|${prefs.neuralSuppression}|${prefs.deepFilter}|${prefs.inputGain !== 100}`;
    if (stageKey === this.appliedStageKey) {
      // Stages unchanged — but the DeepFilterNet intensity slider may have
      // moved. Hot-apply it through the worklet port instead of rebuilding
      // the whole graph + replaceTrack (which would glitch the audio).
      if (prefs.deepFilter && prefs.deepFilterAtten !== this.appliedDfnetAtten) {
        this.audioGraph?.setDfnetAtten(prefs.deepFilterAtten);
        this.appliedDfnetAtten = prefs.deepFilterAtten;
      }
      if (prefs.inputGain !== this.appliedInputGain) {
        // With a live graph this is a cheap GainNode update. If graph creation
        // previously failed, retry it so a non-100% preference never silently
        // falls back to the raw microphone track.
        if (this.audioGraph) {
          this.audioGraph.setInputGain(prefs.inputGain);
          this.appliedInputGain = prefs.inputGain;
        } else if (prefs.inputGain !== 100) {
          const track = await this.rebuildProductionTrack();
          if (track) await this.sfu.replaceAudioTrack(track);
        }
      }
      return;
    }
    const track = await this.rebuildProductionTrack();
    if (!track) return;
    await this.sfu.replaceAudioTrack(track);
  }

  private recordDebug(event: string) {
    // Gate the rolling-buffer slice + ISO string allocation on DEV too.
    // Previously only writeDebug() was DEV-gated, but the array slice +
    // Date stringify ran on every event in prod (~hundreds during a
    // voice session), purely so we could throw away the result.
    if (!import.meta.env.DEV) return;
    this.debugEvents = [...this.debugEvents.slice(-19), `${new Date().toISOString()} ${event}`];
    this.writeDebug();
  }

  private writeDebug() {
    if (!import.meta.env.DEV || typeof window === 'undefined') return;
    const target = window as Window & {
      __bunkerVoiceDebug__?: unknown;
      __bunkerVoiceControls__?: {
        setVideoEnabled: (enabled: boolean) => Promise<boolean>;
        setScreenShareEnabled: (enabled: boolean) => Promise<boolean>;
      };
    };
    target.__bunkerVoiceControls__ = {
      setVideoEnabled: (enabled: boolean) => this.setVideoEnabled(enabled),
      setScreenShareEnabled: (enabled: boolean) => this.setScreenShareEnabled(enabled),
    };
    target.__bunkerVoiceDebug__ = {
      snapshot: {
        activeChannelId: this.snapshot.activeChannelId,
        muted: this.snapshot.muted,
        deafened: this.snapshot.deafened,
        audioBitrateKbps: this.audioBitrateKbps,
        audioProcessingMode: this.audioProcessingMode,
        rtcReady: this.rtcReady,
        sfuConsumerCount: this.sfu.remoteConsumerCount(),
        remoteStreams: this.snapshot.remoteStreams.map((stream) => ({
          userId: stream.userId,
          trackCount: stream.stream.getTracks().length,
          trackKinds: stream.stream.getTracks().map((track) => track.kind),
          mediaTag: stream.mediaTag,
        })),
        localTrackCount: this.snapshot.localStream?.getTracks().length ?? 0,
        localCameraTrackCount: this.snapshot.localCameraStream?.getVideoTracks().length ?? 0,
        localScreenTrackCount: this.snapshot.localScreenStream?.getVideoTracks().length ?? 0,
        error: this.snapshot.error,
      },
      events: this.debugEvents,
    };
  }
}

export const voiceMedia = new VoiceMediaController();

export function useVoiceMediaSnapshot(): VoiceMediaSnapshot {
  return useSyncExternalStore(
    voiceMedia.subscribe,
    voiceMedia.getSnapshot,
    voiceMedia.getSnapshot,
  );
}
